import { useInfiniteQuery } from '@tanstack/react-query';
import { apiGet, operationsClient } from '@/lib/api';
import { leadEndpoints } from '@/lib/api/endpoints';
import { leadKeys } from '@/lib/queryKeys';
import type { LeadListParams, LeadListResponse } from '@/types/lead';

/**
 * Paginated leads query. Used by the merge-duplicate picker, which filters
 * eligibility (survivor/archived/won/converted) client-side after fetching:
 * because that filtering happens after the backend page boundary, the caller
 * pages through results until it has enough eligible rows (or `hasMore` is
 * false) so a page of ineligible leads can't hide a mergeable duplicate.
 */
export function useInfiniteLeads(params: LeadListParams = {}, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: [...leadKeys.list(params), 'infinite'] as const,
    queryFn: ({ pageParam }) =>
      apiGet<LeadListResponse>(operationsClient, leadEndpoints.list(), {
        params: { ...params, page: pageParam } as Record<string, unknown>,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    enabled: options?.enabled,
  });
}
